import { ConversationFormValues } from 'lib-shared-legacy-types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import {
  ConversationDraft,
  ConversationDraftAttachment,
} from 'apis/userStorage';

import {
  WritableConversationDraftMap,
  ConversationDraftPatch,
  ConversationDraftContent,
  ConversationDraftWrite,
} from './conversationDrafts.types';

/** The maximum number of conversation drafts kept in server storage. */
export const MAX_CONVERSATION_DRAFTS = 50;

/** How long draft changes settle before they are written to server storage. */
export const CONVERSATION_DRAFT_DEBOUNCE_MS = 5_000;

/** The longest a draft change waits before it is written. */
export const CONVERSATION_DRAFT_MAX_WAIT_MS = 30_000;

type StoredDraft =
  | WritableConversationDraftMap[string]
  | ConversationDraft
  | null
  | undefined;

/** Narrows the reply form values to what is stored, attachments by descriptor. */
export const toConversationDraftFormValues = (
  formValues: ConversationFormValues,
): NonNullable<ConversationDraft['formValues']> | undefined => {
  const attachments = (formValues.attachments ??
    []) as unknown as readonly ConversationDraftAttachment[];

  const draftFormValues = {
    ...(formValues.fromEmail ? { fromEmail: formValues.fromEmail } : {}),
    ...(formValues.to?.length ? { to: formValues.to } : {}),
    ...(formValues.cc?.length ? { cc: formValues.cc } : {}),
    ...(formValues.bcc?.length ? { bcc: formValues.bcc } : {}),
    ...(attachments.length ? { attachments: [...attachments] } : {}),
  };

  return Object.keys(draftFormValues).length > 0 ? draftFormValues : undefined;
};

/**
 * The composer tab to open for a stored draft. The note tab only when it is
 * the one holding content, so a reply draft is never left hidden behind it.
 */
export const conversationDraftTab = (
  draft: ConversationDraft | null,
): 'reply' | 'note' =>
  !draft?.message?.text && draft?.wrapup ? 'note' : 'reply';

/**
 * What a draft becomes once a change is applied, or null when neither editor
 * holds content. `formValues` alone does not sustain a draft.
 */
export const resolveConversationDraftPatch = (
  storedDraft: StoredDraft,
  { merge, remove, clear }: ConversationDraftPatch,
): ConversationDraftContent | null => {
  if (clear) return null;

  const next = { ...(storedDraft ?? {}), ...merge } as Record<string, unknown>;
  remove?.forEach((field) => delete next[field]);
  delete next.updatedAt;

  // Empty values are dropped rather than stored.
  if (!(next.message as { text?: string } | undefined)?.text) {
    delete next.message;
  }
  if (!next.wrapup) delete next.wrapup;
  if (isEmpty(next.formValues)) delete next.formValues;

  const projection = next as ConversationDraftContent;

  return projection.message?.text || projection.wrapup ? projection : null;
};

/** Whether applying a change leaves the stored draft as it is, ignoring `updatedAt`. */
export const isConversationDraftUnchanged = (
  storedDraft: StoredDraft,
  patch: ConversationDraftPatch,
): boolean => {
  const afterPatch = resolveConversationDraftPatch(storedDraft, patch);
  const asStored = storedDraft
    ? resolveConversationDraftPatch(storedDraft, {})
    : null;

  return isEqual(afterPatch, asStored);
};

/**
 * Applies a draft change to the stored map, evicting the least recently
 * updated entry past the cap. Assigning `null` deletes an entry.
 */
export const applyConversationDraftWrite = (
  drafts: WritableConversationDraftMap,
  { csid, patch, now }: ConversationDraftWrite,
): void => {
  const projection = resolveConversationDraftPatch(drafts[csid], patch);

  drafts[csid] = projection === null ? null : { ...projection, updatedAt: now };

  const evictable = Object.entries(drafts).filter(
    ([key, storedDraft]) => storedDraft != null && key !== csid,
  );

  const liveCount = evictable.length + (drafts[csid] == null ? 0 : 1);
  const overflow = liveCount - MAX_CONVERSATION_DRAFTS;
  if (overflow <= 0) return;

  evictable
    .sort(([, a], [, b]) => (a?.updatedAt ?? 0) - (b?.updatedAt ?? 0))
    .slice(0, overflow)
    .forEach(([key]) => {
      drafts[key] = null;
    });
};
